import { supabase } from './supabase';

const BUCKET_NAME = 'profile-photos';
const MAX_FILE_SIZE = 1024 * 1024; // 1MB

export async function uploadProfilePhoto(file: File, userId: string): Promise<string | null> {
  try {
    if (!file) {
      throw new Error('No file selected');
    }

    if (file.size > MAX_FILE_SIZE) {
      throw new Error('File size must be less than 1MB');
    }

    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    const fileExt = file.name.split('.').pop()?.toLowerCase();
    if (!fileExt || !['jpg', 'jpeg', 'png', 'gif'].includes(fileExt)) {
      throw new Error('Invalid file type. Only JPG, PNG, and GIF are allowed');
    }

    // Create a unique filename
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    // Upload new file
    const { error: uploadError } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(filePath, file, {
        upsert: true,
        cacheControl: '3600',
        contentType: file.type
      });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      throw new Error('Failed to upload photo');
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(filePath);

    // Update profile_photos table
    const { error: dbError } = await supabase
      .from('profile_photos')
      .upsert({
        user_id: userId,
        photo_url: publicUrl
      }, {
        onConflict: 'user_id'
      });

    if (dbError) {
      console.error('Database error:', dbError);
      throw new Error('Failed to update profile photo record');
    }

    return publicUrl;
  } catch (error) {
    console.error('Error uploading profile photo:', error);
    throw error instanceof Error ? error : new Error('Failed to upload photo');
  }
}

export async function deleteProfilePhoto(userId: string): Promise<void> {
  try {
    // Delete from storage
    const { data: files } = await supabase.storage
      .from(BUCKET_NAME)
      .list(userId);

    if (files && files.length > 0) {
      await supabase.storage
        .from(BUCKET_NAME)
        .remove(files.map(file => `${userId}/${file.name}`));
    }

    // Remove from profile_photos table
    await supabase
      .from('profile_photos')
      .delete()
      .eq('user_id', userId);
  } catch (error) {
    console.error('Error deleting profile photo:', error);
    throw error instanceof Error ? error : new Error('Failed to delete photo');
  }
}